import { ref, computed } from 'vue'
import axios from '@/utils/axios'
import useSnackbar, { SnackbarType } from '@/composables/snackbar'
import useAcademySelection from '@/composables/useAcademySelection'
import { getAcademyDisplay, getContentPackItemIds } from '@/utils/contentPackAcademyDisplay'

export default function useSessionAssessments() {
  const RESOURCE = '/assessment/group'
  const { showSnackbar } = useSnackbar()
  const { getContentScopeParams } = useAcademySelection()

  const baseHeaders = [
    { text: 'NAME', value: 'name' },
    {
      text: 'CREATOR',
      value: 'creator',
      sortable: true,
      width: 180,
    },
    {
      text: 'ACADEMY',
      value: 'academy',
      sortable: true,
      width: 180,
    },
    {
      text: 'ACTIVE', value: 'active', width: 100, class: 'compact-table-column', cellClass: 'compact-table-column',
    },
    {
      text: 'ACTIONS',
      value: 'actions',
      sortable: false,
      width: 100,
      align: 'center',
      class: 'compact-table-column',
      cellClass: 'compact-table-column',
    },
  ]

  const dtHeaders = computed(() => [...baseHeaders])

  const dtSearchQuery = ref('')
  const dtExpanded = ref([])

  const blankSessionAssessment = {
    id: '',
    name: '',
    category: '',
    groupType: '',
    sessions: [
      {
        type: '',
        category: '',
        assessments: [
          {
            id: '',
            titleOfSession: '',
            uniqueKey: '',
            sessionType: '',
            category: '',
            level: '',
            frequency: '',
            measurement: '',
            practiceType: '',
            goal: '',
            exercisePriority: '',
            tips: [],
            scoringDescriptions: [],
            exercise: null,
            exerciseUniqueKey: '',
          },
        ],
      },
    ],
  }

  const sessionAssessments = ref([])
  const sessionAssessment = ref(structuredClone(blankSessionAssessment))
  const isLoading = ref(false)

  const createSessionAssessment = async sessionAssessmentItem => {
    isLoading.value = true
    try {
      await axios.post(RESOURCE, sessionAssessmentItem)
      showSnackbar('You have successfully created a new session assessment', SnackbarType.SUCCESS)
    } catch (error) {
      const message = error.response?.data?.message ?? error.message
      showSnackbar(`Failed to create: ${message}`, SnackbarType.ERROR)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const deleteSessionAssessment = async sessionAssessmentItem => {
    isLoading.value = true
    try {
      await axios.delete(`${RESOURCE}/delete/${sessionAssessmentItem.id}`)
      showSnackbar('You have successfully deleted the session assessment', SnackbarType.SUCCESS)
    } catch (error) {
      const message = error.response?.data?.message ?? error.message
      showSnackbar(`Failed to delete: ${message}`, SnackbarType.ERROR)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const fetchSessionAssessments = async () => {
    isLoading.value = true
    const params = { withCreator: true, ...getContentScopeParams() }

    try {
      const [response, contentPacksResponse] = await Promise.all([
        axios.get(RESOURCE, { params }),
        axios.get('/content-pack').catch(() => ({ data: [] })),
      ])
      const contentPackAssessmentIds = ['fitness', 'practice', 'flow'].reduce((result, sectionKey) => ({
        ...result,
        [sectionKey]: getContentPackItemIds(contentPacksResponse.data, sectionKey, 'assessments'),
      }), {})
      sessionAssessments.value = response.data.map(assessment => ({
        ...assessment,
        academy: getAcademyDisplay(
          assessment,
          contentPackAssessmentIds[String(assessment.category || '').toLowerCase()] || new Set(),
        ),
      }))
    } catch (error) {
      const message = error.response?.data?.message ?? error.message
      showSnackbar(`Failed to fetch sessions: ${message}`, SnackbarType.ERROR)
    } finally {
      isLoading.value = false
    }
  }

  const updateSessionAssessment = async sessionAssessmentItem => {
    isLoading.value = true
    try {
      await axios.put(RESOURCE, sessionAssessmentItem)
      showSnackbar('You have successfully updated the session assessment', SnackbarType.SUCCESS)
    } catch (error) {
      const message = error.response?.data?.message ?? error.message
      showSnackbar(`Failed to update: ${message}`, SnackbarType.ERROR)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const updateManySessionAssessments = async (type, paramName, paramValue, ids) => {
    isLoading.value = true
    const payload = {
      type,
      params: [{ name: paramName, value: paramValue, ids }],
    }

    try {
      await axios.put('/library/updateMany', payload)
      showSnackbar(`Successfully updated ${ids.length} item(s)`, SnackbarType.SUCCESS)

      return true
    } catch (error) {
      showSnackbar('Failed to update sessions', SnackbarType.ERROR)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const resetState = () => {
    sessionAssessments.value = []
    sessionAssessment.value = structuredClone(blankSessionAssessment)
    dtSearchQuery.value = ''
    dtExpanded.value = []
    isLoading.value = false
  }

  return {
    dtHeaders,
    dtSearchQuery,
    dtExpanded,
    blankSessionAssessment,
    sessionAssessments,
    sessionAssessment,
    isLoading,
    createSessionAssessment,
    deleteSessionAssessment,
    fetchSessionAssessments,
    updateSessionAssessment,
    updateManySessionAssessments,
    resetState,
  }
}
